import React, { useState, useEffect, useRef } from "react";
import { Modal, Form } from "react-bootstrap";
import CommonButton from "../../commonButton";
import { PlusIcon, Delete02Icon } from "../../../assets/icons";
import PreviewModal from "../previewModal";
import withModal from "../../withModal";
import { useToast } from "../../../context/TostContext";
import InputField from "../../form/InputField";

const ProductModal = ({ onClose, onSubmit, product, isLoading }) => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
  });
  const toast=useToast();
  const [images, setImages] = useState([]); // { file: File, preview: string, id?: string }
  const [existingMediaIds, setExistingMediaIds] = useState([]);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewMediaArray, setPreviewMediaArray] = useState([]);
  const [previewInitialIndex, setPreviewInitialIndex] = useState(0);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (product) {
      setFormData({
        title: product.title || "",
        description: product.description || "",
      });
      const media = product.media || [];
      setImages(media.map(m => ({
        preview: m.file?.url || m.url,
        id: m.id,
        isExisting: true
      })));
      setExistingMediaIds(media.map(m => m.id));
    } else {
      setFormData({ title: "", description: "" });
      setImages([]);
      setExistingMediaIds([]);
    }
  }, [product]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length > 3) {
      toast.error("You can upload a maximum of 3 images");
      return;
    }

    const newImages = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      isExisting: false
    }));

    setImages(prev => [...prev, ...newImages]);
    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeImage = (index) => {
    const imageToRemove = images[index];
    if (imageToRemove.isExisting) {
      setExistingMediaIds(prev => prev.filter(id => id !== imageToRemove.id));
    }

    setImages(prev => {
      const newImages = [...prev];
      if (!imageToRemove.isExisting && imageToRemove.preview) {
        URL.revokeObjectURL(imageToRemove.preview);
      }
      newImages.splice(index, 1);
      return newImages;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (images.length === 0) {
      toast.error("At least 1 image is required");
      return;
    }
    const data = new FormData();
    data.append("title", formData.title);
    if (formData.description.trim()) {
      data.append("description", formData.description);
    }

    images.forEach(img => {
      if (!img.isExisting && img.file) {
        data.append("files", img.file);
      }
    });

    if (product) {
      // Send existing media IDs to keep
      existingMediaIds.forEach(id => {
        data.append("existing_media_ids[]", id);
      });
    }

    onSubmit(data);
  };

  return (
    <>
      <Form onSubmit={handleSubmit}>
        <Modal.Body className="grid row-gap-3">
          <InputField
            className="g-col-12"
            label={{ name: "Product Title", class: "small fw-medium" }}
            required
            input={{
              type: "text",
              name: "title",
              value: formData.title,
              onChange: handleChange,
              placeholder: "Enter product title",
              className: "custom-input",
            }}
          />
          <InputField
            className="g-col-12"
            label={{ name: "Description", class: "small fw-medium" }}
            input={{
              as: "textarea",
              rows: 4,
              name: "description",
              value: formData.description,
              onChange: handleChange,
              placeholder: "Enter product description",
              className: "custom-input",
            }}
          />
          <Form.Group className="g-col-12">
            <Form.Label className="small fw-medium">Media (Max 3 Images) <span className="text-danger">*</span></Form.Label>
            <div className="d-flex flex-wrap gap-3 mt-2">
              {images.map((img, index) => (
                <div key={index} className="position-relative border rounded overflow-hidden" style={{ width: "100px", height: "100px" }}>
                  <img
                    src={img.preview}
                    alt="preview"
                    className="w-100 h-100 object-fit-cover"
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      const mediaArray = images.map(item => ({
                        url: item.preview,
                        title: formData.title || "Product Image",
                        type: "image"
                      }));
                      setPreviewMediaArray(mediaArray);
                      setPreviewInitialIndex(index);
                      setShowPreviewModal(true);
                    }}
                  />
                  <button
                    type="button"
                    className="position-absolute top-0 end-0 bg-danger text-white border-0 p-1 d-flex align-items-center justify-content-center"
                    style={{ borderBottomLeftRadius: "4px" }}
                    onClick={() => removeImage(index)}
                  >
                    <Delete02Icon size={14} />
                  </button>
                </div>
              ))}
              {images.length < 3 && (
                <div
                  className="border rounded d-flex align-items-center justify-content-center cursor-pointer bg-light"
                  style={{ width: "100px", height: "100px", borderStyle: "dashed" }}
                  onClick={() => fileInputRef.current.click()}
                >
                  <PlusIcon size={24} />
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="d-none"
                    accept="image/*"
                    multiple
                    onChange={handleImageChange}
                  />
                </div>
              )}
            </div>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 pb-4 justify-content-center">
          <CommonButton
            type="button"
            title="Cancel"
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
            className="me-2"
          />
          <CommonButton
            title={product ? "Update" : "Save"}
            variant="primary"
            type="submit"
            isLoading={isLoading}
          />
        </Modal.Footer>
      </Form>
      <PreviewModal
        show={showPreviewModal}
        onHide={() => setShowPreviewModal(false)}
        previewMedia={previewMediaArray}
        initialIndex={previewInitialIndex}
      />
    </>
  );
};

export default withModal(ProductModal);
